import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PlacePicker from "@/components/PlacePicker";
import ColorPickerField from "@/components/ColorPickerField";
import PriceGuard from "@/components/PriceGuard";
import AxonautQuotationPicker from "@/components/AxonautQuotationPicker";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PencilSimple, Coin } from "@phosphor-icons/react";
import { CHANTIER_TYPE_META } from "@/pages/chantier/constants";

/** Compact chantier edit — nom, type, adresse (avec PlacePicker + lat/lng auto). */
export default function EditChantierDialog({ open, onOpenChange, chantier, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!chantier) return;
    setForm({
      name: chantier.name || "",
      type: chantier.type || "",
      types: Array.isArray(chantier.types) && chantier.types.length
        ? chantier.types
        : (chantier.type ? [chantier.type] : []),
      color: chantier.color || null,
      address: chantier.address || "",
      postal_code: chantier.postal_code || "",
      city: chantier.city || "",
      latitude: chantier.latitude ?? null,
      longitude: chantier.longitude ?? null,
      revenue_amount_ht: chantier.revenue_amount_ht ?? "",
      axonaut_quotation_id: chantier.axonaut_quotation_id ?? null,
    });
  }, [chantier]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (t) => setForm((f) => {
    const cur = f.types || [];
    const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
    return { ...f, types: next, type: next[0] || "" };
  });

  const onPickAddress = (p) => {
    setForm((f) => ({
      ...f,
      address: p.address || p.label || "",
      postal_code: p.postal_code || null,
      city: p.city || null,
      latitude: p.latitude ?? null,
      longitude: p.longitude ?? null,
    }));
  };

  const save = async () => {
    if (!form.name || !form.name.trim()) { toast.error("Le nom du chantier est requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ["address", "postal_code", "city"].forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (!payload.type) payload.type = null;
      payload.revenue_amount_ht = payload.revenue_amount_ht === "" || payload.revenue_amount_ht == null
        ? null
        : Number(payload.revenue_amount_ht);
      await api.patch(`/chantiers/${chantier.id}`, payload);
      toast.success("Chantier mis à jour");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!chantier) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-chantier-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <PencilSimple size={20} className="text-[#005CFF]" weight="duotone" /> Modifier le chantier
          </DialogTitle>
          <DialogDescription className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
            {chantier.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Nom *</Label>
            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-ch-name" />
          </div>

          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">
              Domaines <span className="text-white/40 normal-case tracking-normal ml-1">(un ou plusieurs)</span>
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1" data-testid="edit-ch-type">
              {Object.entries(CHANTIER_TYPE_META).map(([v, m]) => {
                const on = (form.types || []).includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleType(v)}
                    className={`h-10 rounded-xl border text-xs font-semibold transition-all flex items-center justify-center gap-1 ${on ? "text-white" : "bg-white/5 border-white/10 text-white/60"}`}
                    style={on ? { background: m.color + "22", borderColor: m.color, color: "#fff" } : {}}
                    data-testid={`edit-ch-type-${v}`}
                  >
                    <span>{m.icon}</span>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Adresse</Label>
            <div className="mt-1">
              <PlacePicker
                value={form.address}
                onChange={(v) => set("address", v)}
                onPick={onPickAddress}
                placeholder="Rechercher une adresse…"
                testid="edit-ch-address-picker"
              />
            </div>
            {form.postal_code && form.city && (
              <div className="text-[9px] text-emerald-400 mt-1">
                📍 {form.postal_code} {form.city}
                {form.latitude != null && ` · ${form.latitude.toFixed(4)}, ${form.longitude.toFixed(4)}`}
              </div>
            )}
          </div>

          <ColorPickerField
            value={form.color}
            onChange={(v) => set("color", v)}
            testid="edit-ch-color"
            label="Couleur du chantier"
          />

          <PriceGuard>
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest flex items-center gap-1">
                <Coin size={11} weight="duotone" className="text-emerald-300" /> Montant intervention HT (€)
              </Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="ex. 4200"
                value={form.revenue_amount_ht ?? ""}
                onChange={(e) => set("revenue_amount_ht", e.target.value)}
                className="bg-white/5 border-white/10 h-10 mt-1"
                data-testid="edit-ch-revenue"
              />
              <div className="text-[9px] text-white/40 mt-1">Utilisé par le dashboard Rentabilité pour calculer la marge.</div>
            </div>
          </PriceGuard>

          <AxonautQuotationPicker
            value={form.axonaut_quotation_id}
            onChange={(id) => set("axonaut_quotation_id", id)}
            clientId={chantier?.client_id}
            testidPrefix="edit-ch-axo"
          />
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="flex-1 border border-white/10 hover:bg-white/5">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="edit-ch-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
